import { WithTheme } from '@issuemanager/apps/client/theme';
import {
  Popper,
  styled,
  useEventCallback,
  type PopperProps,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

export const StyledPopper = styled(Popper)<WithTheme<PopperProps>>(
  ({ theme }) => ({
    zIndex: theme.zIndex.modal,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '4px 4px 0 0',
  }),
);

export interface UseAnchorElReturn {
  anchorEl: HTMLElement | null;
  open: boolean;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  handleClose: () => void;
}
export function useAnchorEl(): UseAnchorElReturn {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = useEventCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
  );

  const handleKeyDown = useEventCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
      }
    },
  );

  const handleClose = useEventCallback(() => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  });

  const open = Boolean(anchorEl);

  return { anchorEl, open, handleClick, handleKeyDown, handleClose };
}

export interface DataGridPopperProps {
  renderChildren: (props: UseAnchorElReturn) => React.ReactNode;
  renderTarget: (props: UseAnchorElReturn) => React.ReactNode;
  onClickOutside?: () => void;
}
export function ContainerPopper(props: DataGridPopperProps) {
  const { renderTarget, renderChildren } = props;

  const anchorProps = useAnchorEl();

  // Refs for the Popper and its content
  const popperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node)
      ) {
        props.onClickOutside?.();
        anchorProps.handleClose();
      }
    };

    if (anchorProps.open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorProps.open, anchorProps.handleClose, anchorProps]);

  return (
    <>
      {renderTarget(anchorProps)}

      <StyledPopper
        ref={popperRef}
        open={anchorProps.open}
        anchorEl={anchorProps.anchorEl}
        placement='bottom-start'
        style={{ width: anchorProps.anchorEl?.clientWidth, minWidth: 300 }}
      >
        {renderChildren(anchorProps)}
      </StyledPopper>
    </>
  );
}
